import React from 'react';
import { useTranslation } from 'react-i18next';
import StatTooltip from './StatTooltip';

// The usage logs behind /dashboard only start on 2025-12-08 and showed
// 53,387 minutes on 2026-09-19; the app predates them, so the figure is set
// to 60K+ to cover the earlier period. Refresh with `npm run stats`. The
// tooltip title in public/locales/*/translation.json carries the same figure.
const MINUTES_TRANSCRIBED = '60K+';
// Store downloads across iOS, Android and macOS; not in the usage logs.
const DOWNLOADS = '15K+';

const StatsSection = () => {
  const { t } = useTranslation();

  return (
    <section
      style={{
        background: 'var(--v2-bg)',
        color: 'var(--v2-ink)',
        position: 'relative',
        zIndex: 2,
        boxShadow: '0 -16px 48px rgba(0, 0, 0, 0.18)',
      }}
      className="py-20"
    >
      {/* Wave SVG — protrudes upward over the hero; visible in light mode as the scroll-over edge */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1440 72"
        preserveAspectRatio="none"
        style={{
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: '72px',
          transform: 'translateY(-100%)',
          zIndex: 0, pointerEvents: 'none', display: 'block',
        }}
      >
        <path
          d="M0,48 C240,72 480,24 720,48 C960,72 1200,24 1440,48 L1440,72 L0,72 Z"
          fill="var(--v2-bg)"
        />
      </svg>
      <div className="container mx-auto max-w-6xl px-4">
        <div className="text-center mb-16">
          <h2
            style={{
              fontSize: 'clamp(28px, 3.5vw, 40px)',
              fontWeight: 800,
              color: 'var(--v2-ink)',
              marginBottom: '12px',
            }}
          >
            {t('home.trust.title')}
          </h2>
          <p
            style={{
              fontSize: '18px',
              color: 'var(--v2-ink-muted)',
            }}
          >
            {t('home.trust.subtitle')}
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '32px',
          }}
        >
          <StatTooltip
            content={{
              title: t('home.trust.popups.minutes.title'),
              description: t('home.trust.popups.minutes.description'),
              details: Array.isArray(
                t('home.trust.popups.minutes.details', { returnObjects: true })
              )
                ? (t('home.trust.popups.minutes.details', { returnObjects: true }) as string[])
                : [],
            }}
            className="text-center"
          >
            <div
              className="tnum"
              style={{
                fontSize: 'clamp(36px, 4.5vw, 52px)',
                letterSpacing: '-0.02em',
                fontWeight: 800,
                color: 'var(--v2-primary)',
                marginBottom: '6px',
                lineHeight: 1,
              }}
            >
              {MINUTES_TRANSCRIBED}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--v2-ink-muted)' }}>
              {t('home.trust.stats.minutes')}
            </div>
          </StatTooltip>

          <StatTooltip
            content={{
              title: t('home.trust.popups.downloads.title'),
              description: t('home.trust.popups.downloads.description'),
              details: Array.isArray(
                t('home.trust.popups.downloads.details', { returnObjects: true })
              )
                ? (t('home.trust.popups.downloads.details', { returnObjects: true }) as string[])
                : [],
            }}
            className="text-center"
          >
            <div
              className="tnum"
              style={{
                fontSize: 'clamp(36px, 4.5vw, 52px)',
                letterSpacing: '-0.02em',
                fontWeight: 800,
                color: 'var(--v2-primary)',
                marginBottom: '6px',
                lineHeight: 1,
              }}
            >
              {DOWNLOADS}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--v2-ink-muted)' }}>
              {t('home.trust.stats.downloads')}
            </div>
          </StatTooltip>

          <StatTooltip
            content={{
              title: t('home.trust.popups.languages.title'),
              description: t('home.trust.popups.languages.description'),
              details: Array.isArray(
                t('home.trust.popups.languages.details', { returnObjects: true })
              )
                ? (t('home.trust.popups.languages.details', { returnObjects: true }) as string[])
                : [],
            }}
            className="text-center"
          >
            <div
              className="tnum"
              style={{
                fontSize: 'clamp(36px, 4.5vw, 52px)',
                letterSpacing: '-0.02em',
                fontWeight: 800,
                color: 'var(--v2-primary)',
                marginBottom: '6px',
                lineHeight: 1,
              }}
            >
              30+
            </div>
            <div style={{ fontSize: '14px', color: 'var(--v2-ink-muted)' }}>
              {t('home.trust.stats.languages')}
            </div>
          </StatTooltip>

          <StatTooltip
            content={{
              title: t('home.trust.popups.rating.title'),
              description: t('home.trust.popups.rating.description'),
              details: Array.isArray(
                t('home.trust.popups.rating.details', { returnObjects: true })
              )
                ? (t('home.trust.popups.rating.details', { returnObjects: true }) as string[])
                : [],
            }}
            className="text-center"
          >
            <div
              className="tnum"
              style={{
                fontSize: 'clamp(36px, 4.5vw, 52px)',
                letterSpacing: '-0.02em',
                fontWeight: 800,
                color: 'var(--v2-primary)',
                marginBottom: '6px',
                lineHeight: 1,
              }}
            >
              4.9★
            </div>
            <div style={{ fontSize: '14px', color: 'var(--v2-ink-muted)' }}>
              {t('home.trust.stats.rating')}
            </div>
          </StatTooltip>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
